import {
  PeriodLayout,
  previewPeriodCount,
  clampPreviewPeriodCount,
  hasLimitedPeriodPreview,
  maximumPreviewPeriods,
} from "./periodLayout";
import {
  Box,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useElementSize } from "hooks/useElementSize";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { useState } from "react";
import LocalisedPlot from "components/plotly/LocalisedPlot";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import {
  InventoryTargetsByPeriod,
  PeriodInventoryTargets,
} from "./ruleParameters";
import { effectiveInventoryTargets } from "./inventoryTargetViews";
import { PeriodHorizonControls } from "./PeriodHorizonControls";

export const PeriodTargetsPopover = ({
  material,
  hasBounds,
  rules,
  fixedPeriods,
  periodLayout,
  labelVisibility,
}: {
  material: string;
  hasBounds: boolean;
  rules: PeriodInventoryTargets[];
  fixedPeriods?: InventoryTargetsByPeriod;
  periodLayout: PeriodLayout;
  labelVisibility?: "visible" | "hidden";
}) => {
  const { t } = useTenantTranslation();
  const { ref, size } = useElementSize();
  const units = useUnitsFormatter(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [deployable, setDeployable] = useState(() =>
    previewPeriodCount(periodLayout.deployable)
  );
  const [forecast, setForecast] = useState(() =>
    previewPeriodCount(periodLayout.forecast)
  );
  const previewDeployable = clampPreviewPeriodCount(
    deployable,
    periodLayout.deployable
  );
  const previewForecast = clampPreviewPeriodCount(
    forecast,
    periodLayout.forecast
  );
  const effective =
    anchor !== null
      ? (fixedPeriods ??
        effectiveInventoryTargets({
          rules,
          deployable: previewDeployable,
          forecast: previewForecast,
        }))
      : {};
  const resolved = Object.fromEntries(
    Object.entries(effective).map(([period, targets]) => [
      period,
      targets.find((target) => target.material_name === material),
    ])
  );
  const periods = Array.from(
    { length: Math.max(0, ...Object.keys(resolved).map(Number)) },
    (_, index) => index + 1
  );
  return (
    <>
      <Tooltip
        title={labelVisibility === "hidden" ? "" : t("autoPeriodPreview")}
      >
        <span>
          <IconButton
            disabled={!hasBounds}
            size="small"
            aria-label={t("autoPeriodPreview")}
            onClick={(event) => {
              setAnchor(event.currentTarget);
            }}
          >
            <ShowChartIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack gap={2} sx={{ p: 2, width: { xs: 310, sm: 520 } }}>
          <Typography variant="h6">{t(material)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("autoPeriodPreviewExplanation")}
          </Typography>
          {!fixedPeriods && hasLimitedPeriodPreview(periodLayout) ? (
            <Typography variant="body2" color="text.secondary">
              {t("autoLimitedPeriodPreview", { count: maximumPreviewPeriods })}
            </Typography>
          ) : null}
          {!fixedPeriods ? (
            <PeriodHorizonControls
              periodLayout={periodLayout}
              deployable={previewDeployable}
              forecast={previewForecast}
              onDeployableChange={setDeployable}
              onForecastChange={setForecast}
            />
          ) : null}
          <Box ref={ref} sx={{ "& .js-plotly-plot": { width: "100%" } }}>
            <LocalisedPlot
              data={(["min_quantity", "max_quantity"] as const).map(
                (bound) => ({
                  type: "scatter",
                  mode: "lines+markers",
                  name: t(bound === "min_quantity" ? "min" : "max"),
                  x: periods,
                  y: periods.map(
                    (period) => resolved[String(period)]?.[bound] ?? null
                  ),
                  connectgaps: false,
                  line: { shape: "hv" },
                })
              )}
              layout={{
                height: 260,
                width: size?.width,
                autosize: true,
                margin: { l: 55, r: 15, t: 10, b: 70 },
                xaxis: { title: { text: t("period") }, dtick: 1 },
                yaxis: { title: { text: units("mass") } },
                legend: { orientation: "h", y: -0.3 },
              }}
              config={{ displayModeBar: false, responsive: true }}
            />
          </Box>
        </Stack>
      </Popover>
    </>
  );
};
